import {useSearchParam} from '@raison/navigation';
import {expectEither, POST, useHttpClient} from '@raison/network-facade';
import {RsnDialog} from '@raison/ui-kit';
import {queryOptions, useMutation, useQuery, useSuspenseQuery} from '@tanstack/react-query';
import {type} from 'arktype';
import {absurd} from 'fp-ts/lib/function';
import React, {useRef, useState} from 'react';
import {Navigate, useNavigate} from 'react-router';
import {GenericErrorMsg} from '~/entities/dialogs';
import {useUserInfoQueryOptions} from '~/entities/user';
import {
    getExpiryMsFromNow,
    InvalidCodeDialog,
    PinErrorCode,
    SusActivityDialog,
    TwoFaAppSetupView,
    TwoFaCodeForm,
    useResendTimerFromQuery,
} from '~/features/two-factor';
import {useAppDispatch} from '~/shared/store';

const TIMER_SECONDS = 60;
const TOTAL_ATTEMPTS = 5;

export namespace AuthenticationSetting {
    export const On = ActivatingAuthenticator;
    export const Off = DeactivatingAuthenticator;
}

function ActivatingAuthenticator({pathBack}: {pathBack: string}) {
    const queryOfUser = useSuspenseQuery(useUserInfoQueryOptions());
    const flowIsValid = queryOfUser.data.otpEnabled === false;

    const navigate = useNavigate();
    const [step = 'email-check', setStep] = useSearchParam<'email-check' | 'authenticator-check' | 'error'>('step');
    const [dialog, setDialog] = useSearchParam<'invalid-code' | 'suspicious-activity'>('dialog');

    const [emailPin, setEmailPin] = useState('');
    const [totpPin, setTotpPin] = useState('');

    const clearPin = () => {
        if (step === 'email-check') {
            setEmailPin('');
        }
        if (step === 'authenticator-check') {
            setTotpPin('');
        }
    };

    const queryOfInit = useQuery({
        ...useTotpEmailRequestOps('init'),
        enabled: flowIsValid,
    });

    const timer$ = useResendTimerFromQuery(queryOfInit);

    const totpPrepareMutation = useMutation({
        ...useTotpPrepareMutationOps(),
        onSuccess(result) {
            if (result.ok) {
                setStep('authenticator-check', {replace: true});
            }
            if (result.error === PinErrorCode.InvalidCode) {
                setEmailPin('');
                setDialog('invalid-code');
            }
            if (result.error === PinErrorCode.SusActivity) {
                setDialog('suspicious-activity');
            }
            if (result.error === PinErrorCode.Unknown) {
                setDialog('error');
            }
        },
    });

    const totpSetMutation = useMutation({
        ...useSetTotpMutationOptions(),
        async onSuccess(result) {
            if (result.ok) {
                queryOfUser.refetch();
                navigate(pathBack, {replace: true});
                return;
            }
            if (result.error === PinErrorCode.InvalidCode) {
                setTotpPin('');
                setDialog('invalid-code');
            }
        },
    });

    let view = <Navigate replace to={pathBack} />;
    if (!flowIsValid) {
        return view;
    }
    if (step === 'authenticator-check' && totpPrepareMutation.data?.ok) {
        const {secret, url, uuid} = totpPrepareMutation.data;
        view = (
            <TwoFaAppSetupView
                setupUrl={url}
                setupKey={secret}
                generatedCode={totpPin}
                onGeneratedCodeChange={setTotpPin}
                onConfirm={() => {
                    totpSetMutation.mutate({uuid, totpPin});
                }}
                isPending={totpSetMutation.isPending}
            />
        );
    }
    if (step === 'email-check') {
        view = (
            <TwoFaCodeForm
                heading={<TwoFaCodeForm.AppActivationHeader userEmail={queryOfUser.data?.email ?? ''} />}
                timer$={timer$}
                onRetryIntent={() => {
                    queryOfInit.refetch();
                }}
                deliveryInfo={null}
                otp={emailPin}
                onOtpChange={setEmailPin}
                otpLength={6}
                onInputComplete={() => {
                    if (!queryOfInit.data) {
                        return;
                    }
                    totpPrepareMutation.mutate({uuid: queryOfInit.data.uuid, emailPin});
                }}
                isPending={totpPrepareMutation.isPending}
                issue={totpPrepareMutation.data?.issue}
            />
        );
    }

    return (
        <div className='flex-1 p-4'>
            {view}
            <RsnDialog.Controller onClose={() => navigate(-1)}>
                {(() => {
                    return (
                        (dialog === 'invalid-code' && (
                            <InvalidCodeDialog
                                onClose={() => {
                                    clearPin();
                                    TwoFaCodeForm.focusOTPInput();
                                }}
                            />
                        )) ||
                        (dialog === 'suspicious-activity' && <SusActivityDialog />) ||
                        (dialog === 'error' && <GenericErrorMsg.Dialog />)
                    );
                })()}
            </RsnDialog.Controller>
        </div>
    );
}

function DeactivatingAuthenticator({pathBack}: {pathBack: string}) {
    const queryOfUser = useSuspenseQuery(useUserInfoQueryOptions());
    const flowIsValid = queryOfUser.data.otpEnabled === true;

    const navigate = useNavigate();
    const [dialog, setDialog] = useSearchParam<'invalid-code' | 'suspicious-activity' | 'error'>('dialog');

    const queryOfInit = useQuery({
        ...useTotpEmailRequestOps('remove'),
        enabled: flowIsValid,
    });
    const timer$ = useResendTimerFromQuery(queryOfInit);

    const [emailPin, setEmailPin] = useState('');
    const clearEmailPin = () => setEmailPin('');

    const deleteTotpMutation = useMutation({
        ...useTotpDeleteMutationOps(),
        async onSuccess(result) {
            if (result.ok) {
                queryOfUser.refetch();
                navigate(pathBack, {replace: true});
            }
            if (result.error === PinErrorCode.InvalidCode) {
                setDialog('invalid-code');
            }
            if (result.error === PinErrorCode.SusActivity) {
                setDialog('suspicious-activity');
            }
            if (result.error === PinErrorCode.Unknown) {
                setDialog('error');
            }
        },
    });

    let view = <Navigate replace to={pathBack} />;
    if (!flowIsValid) {
        return view;
    }
    view = (
        <TwoFaCodeForm
            heading={<TwoFaCodeForm.AppDeactivationHeader userEmail={queryOfUser.data?.email ?? ''} />}
            timer$={timer$}
            onRetryIntent={() => {
                queryOfInit.refetch();
            }}
            deliveryInfo={null}
            otp={emailPin}
            onOtpChange={setEmailPin}
            otpLength={6}
            onInputComplete={() => {
                if (!queryOfInit.data) {
                    return;
                }
                deleteTotpMutation.mutate({uuid: queryOfInit.data.uuid, emailPin});
            }}
            isPending={deleteTotpMutation.isPending}
            issue={deleteTotpMutation.data?.issue}
        />
    );
    return (
        <div className='flex-1 p-4'>
            {view}
            <RsnDialog.Controller onClose={() => navigate(-1)}>
                {(() => {
                    return (
                        (dialog === 'invalid-code' && (
                            <InvalidCodeDialog
                                onClose={() => {
                                    clearEmailPin();
                                    TwoFaCodeForm.focusOTPInput();
                                }}
                            />
                        )) ||
                        (dialog === 'suspicious-activity' && <SusActivityDialog />) ||
                        (dialog === 'error' && <GenericErrorMsg.Dialog />)
                    );
                })()}
            </RsnDialog.Controller>
        </div>
    );
}

function useTotpEmailRequestOps(intent: 'init' | 'remove') {
    const http = useHttpClient();
    const attemptsLeftRef = useRef(TOTAL_ATTEMPTS);
    /** intentionally not using templates to make urls grep-able */
    const url = {
        init: 'v1/otp/init',
        remove: 'v1/otp/remove',
    }[intent];
    const {gcTime, queryKey, queryFn} = queryOptions({
        gcTime: 0,
        queryKey: [url],
        async queryFn({client, queryKey}) {
            const response = await http(url, POST()).then(
                ...expectEither({
                    200: type({uuid: 'string.uuid'}),
                })
            );
            if (response.status === 'Abort') {
                await client.cancelQueries({queryKey});
                throw response.reason;
            }
            const attemptsLeft = (attemptsLeftRef.current -= 1);
            return {
                uuid: response.json.uuid,
                expiresAt: getExpiryMsFromNow(TIMER_SECONDS),
                attemptsLeft,
            };
        },
    });
    return {gcTime, queryKey, queryFn};
}

function useTotpPrepareMutationOps() {
    const http = useHttpClient();
    const dispatch = useAppDispatch();
    return {
        async mutationFn({uuid, emailPin: pin}: {uuid: string; emailPin: string}) {
            const response = await http('v1/otp/create', POST({uuid, pin})).then(
                ...expectEither({
                    200: type({
                        uuid: 'string.uuid',
                        secret: 'string',
                        url: 'string',
                    }),
                    400: type({message: 'string'}),
                })
            );
            if (response.status === 'Abort') {
                return {ok: false} as const;
            }
            if (response.status === 400) {
                const {message} = response.json;
                if (message === PinErrorCode.ExpiredCode) {
                    const issueText = 'This code has expired';
                    return {ok: false, issue: issueText} as const;
                }
                if (message === PinErrorCode.InvalidCode) {
                    return {ok: false, error: PinErrorCode.InvalidCode} as const;
                }
                if (message === PinErrorCode.SusActivity) {
                    return {ok: false, error: PinErrorCode.SusActivity} as const;
                }
                return {ok: false, error: PinErrorCode.Unknown} as const;
            }
            return {...response.json, ok: true} as const;
        },
        onError() {
            dispatch(GenericErrorMsg.open());
        },
    };
}

function useTotpDeleteMutationOps() {
    const http = useHttpClient();
    return {
        async mutationFn({uuid, emailPin: pin}: {uuid: string; emailPin: string}) {
            const response = await http('v1/otp/reset', POST({uuid, pin})).then(
                ...expectEither({
                    200: null,
                    400: type({message: 'string'}),
                })
            );
            if (response.status === 'Abort') {
                return {ok: false} as const;
            }
            if (response.status === 400) {
                const {message} = response.json;
                if (message === PinErrorCode.ExpiredCode) {
                    const issueText = 'This code has expired';
                    return {ok: false, issue: issueText} as const;
                }
                if (message === PinErrorCode.InvalidCode) {
                    return {ok: false, error: PinErrorCode.InvalidCode} as const;
                }
                if (message === PinErrorCode.SusActivity) {
                    return {ok: false, error: PinErrorCode.SusActivity} as const;
                }
                return {ok: false, error: PinErrorCode.Unknown} as const;
            }
            return {ok: true} as const;
        },
    };
}

function useSetTotpMutationOptions() {
    const http = useHttpClient();
    const dispatch = useAppDispatch();
    return {
        async mutationFn({uuid, totpPin: pin}: {uuid: string; totpPin: string}) {
            const response = await http('v1/otp/set', POST({uuid, pin})).then(
                ...expectEither({
                    200: null,
                    400: null,
                })
            );
            if (response.status === 'Abort') {
                return {ok: false} as const;
            }
            if (response.status === 200) {
                return {ok: true} as const;
            }
            if (response.status === 400) {
                return {ok: false, error: PinErrorCode.InvalidCode} as const;
            }
            return absurd<never>(response);
        },
        onError() {
            dispatch(GenericErrorMsg.open());
        },
    };
}
